import { useEffect, useState } from 'react';
import { Loader2, TrendingUp, Activity, Zap, Calendar } from 'lucide-react';
import { apiGet } from '@/lib/api';

interface UsageInfo {
  tokens: {
    used: number;
    limit: number;
    remaining: number;
    allowed: boolean;
  };
}

interface SubscriptionInfo {
  subscription: {
    planId: string;
    status: string;
    currentPeriodEnd: string | null;
    tokensUsed: number;
    tokensLimit: number;
  };
  plan: { name: string; priceCents: number } | null;
}

interface DailyUsage {
  date: string;
  tokens: number;
  cost: number;
  operations: number;
}

interface UsageHistoryItem {
  id: string;
  operation: string;
  model: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  costUsd: number | null;
  hypemaTokens: number | null;
  webSearchRequests: number | null;
  metadata: Record<string, any> | null;
  createdAt: string;
}

interface UsageHistoryResponse {
  items: UsageHistoryItem[];
  total: number;
  page: number;
  pageSize: number;
}

const OPERATION_LABELS: Record<string, string> = {
  'research-company': 'Company Research',
  'generate-leads': 'Lead Generation',
  'map-csv-columns': 'CSV Mapping',
  'generate-email': 'Email Generation',
  'generate-email-preview': 'Email Preview',
};

function operationDetail(item: UsageHistoryItem): string {
  const m = item.metadata;
  if (!m) return '';
  switch (item.operation) {
    case 'generate-email':
    case 'generate-email-preview': {
      const parts = [m.recipientName, m.recipientCompany].filter(Boolean);
      return parts.length > 0 ? parts.join(' @ ') : '';
    }
    case 'generate-leads':
      return m.industry ? `${m.requestedCount ?? '?'} leads: ${m.industry}` : '';
    case 'research-company':
      return m.domain || '';
    case 'map-csv-columns':
      return m.headerCount ? `${m.headerCount} columns` : '';
    default:
      return '';
  }
}

export function SettingsUsagePage() {
  const [usage, setUsage] = useState<UsageInfo | null>(null);
  const [info, setInfo] = useState<SubscriptionInfo | null>(null);
  const [daily, setDaily] = useState<DailyUsage[]>([]);
  const [history, setHistory] = useState<UsageHistoryResponse | null>(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiGet<UsageInfo>('/billing/usage'),
      apiGet<SubscriptionInfo>('/billing/subscription'),
      apiGet<DailyUsage[]>('/billing/usage-daily'),
      apiGet<UsageHistoryResponse>('/billing/usage-history?page=1&pageSize=20'),
    ]).then(([usg, sub, dly, hist]) => {
      setUsage(usg);
      setInfo(sub);
      setDaily(dly ?? []);
      setHistory(hist);
    }).finally(() => setLoading(false));
  }, []);

  const [initialLoad, setInitialLoad] = useState(true);
  useEffect(() => {
    if (initialLoad) { setInitialLoad(false); return; }
    apiGet<UsageHistoryResponse>(`/billing/usage-history?page=${historyPage}&pageSize=20`)
      .then(setHistory);
  }, [historyPage]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-primary" />
      </div>
    );
  }

  const tokens = usage?.tokens;
  const sub = info?.subscription;
  const plan = info?.plan;
  const unlimited = tokens && tokens.limit === -1;
  const pct = !tokens || unlimited ? 0 : Math.min((tokens.used / tokens.limit) * 100, 100);
  const isNear = !unlimited && pct >= 80;
  const isOver = !unlimited && pct >= 100;

  const periodEnd = sub?.currentPeriodEnd
    ? new Date(sub.currentPeriodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  // Daily chart calculations
  const maxDailyTokens = Math.max(1, ...daily.map((d) => d.tokens));

  return (
    <div className="space-y-8 max-w-2xl mx-auto w-full animate-fade-in">
      <div>
        <h1 className="text-xl font-bold tracking-tight">Usage</h1>
        <p className="text-sm text-base-content/40 mt-1">Monitor your token consumption and limits.</p>
      </div>

      {/* Overview cards */}
      {tokens && (
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="glass-card rounded-xl p-5">
            <div className="flex items-center gap-2 text-base-content/40 mb-2">
              <Activity size={14} />
              <span className="text-xs font-semibold uppercase tracking-wider">Used</span>
            </div>
            <p className="text-2xl font-bold tabular-nums">{tokens.used.toLocaleString()}</p>
            <p className="text-xs text-base-content/40 mt-0.5">tokens this period</p>
          </div>
          <div className="glass-card rounded-xl p-5">
            <div className="flex items-center gap-2 text-base-content/40 mb-2">
              <Zap size={14} />
              <span className="text-xs font-semibold uppercase tracking-wider">Limit</span>
            </div>
            <p className="text-2xl font-bold tabular-nums">{unlimited ? '∞' : tokens.limit.toLocaleString()}</p>
            <p className="text-xs text-base-content/40 mt-0.5">tokens per month</p>
          </div>
          <div className="glass-card rounded-xl p-5">
            <div className="flex items-center gap-2 text-base-content/40 mb-2">
              <TrendingUp size={14} />
              <span className="text-xs font-semibold uppercase tracking-wider">Remaining</span>
            </div>
            <p className={`text-2xl font-bold tabular-nums ${isOver ? 'text-error' : isNear ? 'text-warning' : ''}`}>
              {unlimited ? '∞' : tokens.remaining.toLocaleString()}
            </p>
            <p className="text-xs text-base-content/40 mt-0.5">tokens available</p>
          </div>
        </div>
      )}

      {/* Usage bar */}
      {tokens && (
        <div className="space-y-1">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
            Token Usage This Period
          </h2>
          <div className="glass-card rounded-xl p-5">
            {!unlimited ? (
              <>
                {/* Large percentage display */}
                <div className="flex items-end justify-between mb-4">
                  <div>
                    <p className={`text-4xl font-bold tabular-nums ${isOver ? 'text-error' : isNear ? 'text-warning' : 'text-base-content'}`}>
                      {Math.round(pct)}%
                    </p>
                    <p className="text-xs text-base-content/40 mt-0.5">of your monthly limit used</p>
                  </div>
                  <p className="text-sm text-base-content/40 tabular-nums">
                    {tokens.used.toLocaleString()} / {tokens.limit.toLocaleString()}
                  </p>
                </div>

                {/* Bar */}
                <div className="relative">
                  <div className="w-full h-4 rounded-full bg-base-300 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ease-out ${
                        isOver ? 'bg-error' : isNear ? 'bg-warning' : 'bg-primary'
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>

                  {/* Tick marks */}
                  <div className="flex justify-between mt-1.5 px-0.5">
                    {[0, 25, 50, 75, 100].map((tick) => (
                      <span key={tick} className="text-[10px] text-base-content/30 tabular-nums">{tick}%</span>
                    ))}
                  </div>
                </div>

                {/* Status text */}
                <p className={`text-xs mt-3 ${isOver ? 'text-error font-medium' : isNear ? 'text-warning font-medium' : 'text-base-content/40'}`}>
                  {tokens.remaining > 0
                    ? `${tokens.remaining.toLocaleString()} tokens remaining`
                    : 'No tokens remaining — upgrade to continue'}
                </p>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Zap size={20} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium">Unlimited tokens</p>
                  <p className="text-xs text-base-content/40">{tokens.used.toLocaleString()} tokens used this period — no cap</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Daily usage chart */}
      {daily.length > 0 && (
        <div className="space-y-1">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
            Daily Usage
          </h2>
          <div className="glass-card rounded-xl p-5">
            <div className="flex items-end gap-[2px] h-28">
              {daily.map((d) => {
                const height = Math.max(2, (d.tokens / maxDailyTokens) * 100);
                return (
                  <div
                    key={d.date}
                    className="flex-1 group relative"
                    title={`${d.date}: ${d.tokens} tokens, ${d.operations} ops`}
                  >
                    <div
                      className="w-full bg-primary/70 hover:bg-primary rounded-t transition-colors"
                      style={{ height: `${height}%` }}
                    />
                    <div className="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-[10px] bg-base-300 rounded whitespace-nowrap z-10">
                      {new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}: {d.tokens} tokens
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between mt-2 text-[10px] text-base-content/30">
              {daily.length > 0 && (
                <>
                  <span>{new Date(daily[0].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  <span>{new Date(daily[daily.length - 1].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Usage history table */}
      {history && history.items.length > 0 && (
        <div className="space-y-1">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
            Operation History
          </h2>
          <div className="glass-card rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-base-300 text-left text-xs text-base-content/40 uppercase tracking-wider">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Operation</th>
                  <th className="px-4 py-3 text-right">Tokens</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-base-300/50">
                {history.items.map((item) => (
                  <tr key={item.id} className="hover:bg-base-200/30">
                    <td className="px-4 py-2.5 text-xs text-base-content/60 tabular-nums">
                      {new Date(item.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      <span className="text-base-content/30 ml-1">
                        {new Date(item.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      <span className="font-medium">{OPERATION_LABELS[item.operation] ?? item.operation}</span>
                      {item.webSearchRequests ? (
                        <span className="text-base-content/30 ml-1">+{item.webSearchRequests} search{item.webSearchRequests > 1 ? 'es' : ''}</span>
                      ) : null}
                      {operationDetail(item) && (
                        <span className="block text-[10px] text-base-content/40 mt-0.5 truncate max-w-[280px]">{operationDetail(item)}</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-right tabular-nums font-medium">
                      {item.hypemaTokens ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {history.total > history.pageSize && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-base-300">
                <p className="text-xs text-base-content/40">
                  {((history.page - 1) * history.pageSize) + 1}–{Math.min(history.page * history.pageSize, history.total)} of {history.total}
                </p>
                <div className="flex gap-2">
                  <button
                    className="btn btn-xs btn-ghost"
                    disabled={history.page <= 1}
                    onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                  >
                    Prev
                  </button>
                  <button
                    className="btn btn-xs btn-ghost"
                    disabled={history.page * history.pageSize >= history.total}
                    onClick={() => setHistoryPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Plan & period info */}
      <div className="space-y-1">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
          Current Period
        </h2>
        <div className="glass-card rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">{plan?.name ?? 'Free'} plan</p>
              {periodEnd && (
                <p className="text-xs text-base-content/40 mt-0.5">
                  Resets on {periodEnd}
                </p>
              )}
              {!periodEnd && (
                <p className="text-xs text-base-content/40 mt-0.5">
                  Monthly rolling period
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
